import { APIKit } from "config/axios";
import store from "config/redux";
import { end, start } from "config/redux/slice";

import { RouteConstants, ServerConstants, ServiceConstants } from "constant";
import { ServiceModel } from "models";
import {
  MotivationalMessageCurrentResponse,
  MotivationalMessageRequest,
  MotivationalMessageResponse,
} from "types";
import { AppUtils } from "utils";

import { handleServiceError } from "./common";
import * as PublicService from "./public";

const { ROUTE_LOGIN } = RouteConstants;
const { API_V1_MOTIVATIONAL_MESSAGES } = ServerConstants;
const {
  REQUEST_MOTIVATIONAL_MESSAGES_LIST_ID,
  REQUEST_MOTIVATIONAL_MESSAGE_CURRENT_ID,
  REQUEST_MOTIVATIONAL_MESSAGE_VIEW_ID,
  REQUEST_MOTIVATIONAL_MESSAGE_CREATE_ID,
  REQUEST_MOTIVATIONAL_MESSAGE_UPDATE_ID,
  REQUEST_MOTIVATIONAL_MESSAGE_DELETE_ID,
  REQUEST_MOTIVATIONAL_MESSAGE_ACTIVATE_ID,
} = ServiceConstants;

const buildViewRequestId = (id: number) => `${REQUEST_MOTIVATIONAL_MESSAGE_VIEW_ID}${id}`;
const buildUpdateRequestId = (id: number) => `${REQUEST_MOTIVATIONAL_MESSAGE_UPDATE_ID}${id}`;
const buildDeleteRequestId = (id: number) => `${REQUEST_MOTIVATIONAL_MESSAGE_DELETE_ID}${id}`;
const buildActivateRequestId = (id: number) => `${REQUEST_MOTIVATIONAL_MESSAGE_ACTIVATE_ID}${id}`;

async function retryWithRenewal<T>(request: () => Promise<T>, retryOnUnauthorized = true): Promise<T> {
  try {
    return await request();
  } catch (err: any) {
    const errStatus = JSON.parse(JSON.stringify(err))?.status;

    if (errStatus === 401 && retryOnUnauthorized) {
      const renewed = await PublicService.renew();

      if (renewed) {
        return retryWithRenewal(request, false);
      }

      AppUtils.logout();
      if (window.location.pathname !== ROUTE_LOGIN) {
        window.location.href = ROUTE_LOGIN;
      }
    }

    throw err;
  }
}

async function performTrackedRequest<T extends object | undefined>(
  requestId: string,
  request: () => Promise<T>,
): Promise<T> {
  try {
    store.dispatch(start(requestId));
    const data = await retryWithRenewal(request);
    store.dispatch(end(new ServiceModel(requestId, data)));
    return data;
  } catch (err: any) {
    handleServiceError(requestId, err);
    throw err;
  }
}

export async function getMotivationalMessages(): Promise<MotivationalMessageResponse[]> {
  return performTrackedRequest(REQUEST_MOTIVATIONAL_MESSAGES_LIST_ID, async () => {
    const response = await APIKit.get(API_V1_MOTIVATIONAL_MESSAGES);
    return response.data as MotivationalMessageResponse[];
  });
}

export async function getMotivationalMessage(id: number): Promise<MotivationalMessageResponse> {
  return performTrackedRequest(buildViewRequestId(id), async () => {
    const response = await APIKit.get(`${API_V1_MOTIVATIONAL_MESSAGES}/${id}`);
    return response.data as MotivationalMessageResponse;
  });
}

export async function createMotivationalMessage(
  data: MotivationalMessageRequest,
): Promise<MotivationalMessageResponse> {
  return performTrackedRequest(REQUEST_MOTIVATIONAL_MESSAGE_CREATE_ID, async () => {
    const response = await APIKit.post(API_V1_MOTIVATIONAL_MESSAGES, data);
    return response.data as MotivationalMessageResponse;
  });
}

export async function updateMotivationalMessage(
  id: number,
  data: MotivationalMessageRequest,
): Promise<MotivationalMessageResponse> {
  return performTrackedRequest(buildUpdateRequestId(id), async () => {
    const response = await APIKit.put(`${API_V1_MOTIVATIONAL_MESSAGES}/${id}`, data);
    return response.data as MotivationalMessageResponse;
  });
}

export async function deleteMotivationalMessage(id: number): Promise<void> {
  await performTrackedRequest(buildDeleteRequestId(id), async () => {
    const response = await APIKit.delete(`${API_V1_MOTIVATIONAL_MESSAGES}/${id}`);
    return response.data as undefined;
  });
}

export async function activateMotivationalMessage(id: number): Promise<MotivationalMessageResponse> {
  return performTrackedRequest(buildActivateRequestId(id), async () => {
    const response = await APIKit.patch(`${API_V1_MOTIVATIONAL_MESSAGES}/${id}/activate`);
    return response.data as MotivationalMessageResponse;
  });
}

export async function getCurrentMotivationalMessage(): Promise<MotivationalMessageCurrentResponse> {
  return performTrackedRequest(REQUEST_MOTIVATIONAL_MESSAGE_CURRENT_ID, async () => {
    const response = await APIKit.get(`${API_V1_MOTIVATIONAL_MESSAGES}/current`);
    return response.data as MotivationalMessageCurrentResponse;
  });
}
